/* ============================================================
 * src/screens/operations/_bundle.tsx
 *
 * Mechanical ESM transcription of the v.3 prototype's screens-operations.jsx.
 * The prototype destructured React from a global and exposed each screen
 * via Object.assign(window, ...). This file restores the same surface as
 * named ESM exports; per-component files in this directory re-export the
 * screen the route table maps to so downstream wiring tasks can edit a
 * single component without touching the bundle.
 * ============================================================ */
/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Icon, Btn, Pill, CircaTag, Panel, PageHead, Breadcrumbs, Tabs, Spark,
  StatCard, ScoreRing, HelpDot, TimerChip, AccessGate, AdminGate, CapPill,
  FreshnessBanner, AuditTrailLink, Field, Input, Textarea, Select, Toggle,
  SegmentedControl, Thumb, Avatar, EmptyState, Skeleton, useLoading,
  SkeletonStatRow, SkeletonCardGrid, SkeletonTable, SkeletonPanel, SkeletonList,
  Modal, SlideOver, ConfirmModal, Dropdown, Progress, TONES,
  useToast, useMutationWithToast,
} from '../../components/primitives';
// T318 — the mock fixture import retired. The Operations bundle now reads
// `api.alerts.list` for the alerts list and `useChannelList()` for the
// per-channel feeds row. Decorative tile data with no real public query
// (background-task sparks, automations list) renders honest neutral
// placeholders or empty states.
import { useChannelList } from '../../hooks/useChannelList';
import { ago } from '../../lib/format';
import { navigate } from '../../lib/nav';
import { BatchPatternWizard, VersionDetailModal } from '../pending/_bundle';
import { api } from '@convex/api';
// Convex-wired mutation wrapper (T327 / FR-008). Aliased because the
// primitives barrel still re-exports a legacy in-memory `useMutationWithToast`
// for the un-wired prototype screens; the AutomationBuilder must use the
// real Convex-bound hook from `src/hooks/`.
import { useMutationWithToast as useConvexMutationWithToast } from '../../hooks/useMutationWithToast';
// T333 (FR-012, WIRE-7) — `/version-history` wired to `api.versionHistory.list`
// (workspace-wide, auth-gated, default cap 200). `/compliance` defers
// honestly: there is no public `compliance.listAssets` / `recheck` query
// family today and FR-012 / NFR-006 forbid speculative wiring.
import { useQuery } from 'convex/react';
import { MockBoundary } from '../../components/Provenance';

// Silence unused-import lint for primitives this file may not reference.
void React; void useState; void useEffect; void useRef; void useCallback; void useMemo;
void Icon; void Btn; void Pill; void CircaTag; void Panel; void PageHead; void Breadcrumbs;
void Tabs; void Spark; void StatCard; void ScoreRing; void HelpDot; void TimerChip;
void AccessGate; void AdminGate; void CapPill; void FreshnessBanner; void AuditTrailLink;
void Field; void Input; void Textarea; void Select; void Toggle; void SegmentedControl;
void Thumb; void Avatar; void EmptyState; void Skeleton; void useLoading;
void SkeletonStatRow; void SkeletonCardGrid; void SkeletonTable; void SkeletonPanel; void SkeletonList;
void Modal; void SlideOver; void ConfirmModal; void Dropdown; void Progress; void TONES;
void useToast; void useMutationWithToast;
void navigate;

/* ============================================================
   CIRCA — Operations arc
   ============================================================ */

export function SystemHealth() {
  // T318 — `opsChannels` is the wired channel list driving the
  // "Entrada de señal por canal" panel further down. Loading + empty
  // states are rendered honestly inside the panel.
  const opsChannels = useChannelList();
  const cards = [
    ['Entrada de señal', 'good', '6/6 recibiendo', 'signal'],
    ['Red de entrega', 'good', '99.98% disponible', 'globe'],
    ['Tareas en segundo plano', 'good', '32 saludables', 'refresh'],
    ['Capacidad de borde', 'warn', '62% en uso', 'server'],
  ];
  const lanes = [
    ['Codificador → Ingesta', 'good', 96], ['Ingesta → Producción', 'good', 92],
    ['Producción → Borde', 'good', 88], ['Borde → Espectador', 'warn', 74], ['Publicación → Destino', 'good', 90],
  ];
  return (
    <div>
      <PageHead title="Salud del sistema" description="La señal de hoy en toda la plataforma." actions={<Btn variant="ghost" icon="refresh" size="sm">Actualizar</Btn>} />
      <FreshnessBanner show message="Algunos indicadores de estado pueden ir ligeramente atrasados." />
      {useLoading(600) ? <><SkeletonStatRow /><SkeletonPanel h={180} /><div style={{ height: 24 }} /><SkeletonList rows={5} /></> : <>
      <MockBoundary detail="Indicadores de demostración — sólo el feed en vivo de tareas usa datos reales." style={{ marginBottom: 24 }}>
      <div className="circa-grid-4">
        {cards.map(([l, tone, metric, icon]) => (
          <div key={l} style={{ border: '1px solid var(--border-cool)', borderRadius: 22, padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
              <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', display: 'flex', alignItems: 'center', gap: 7 }}><Icon name={icon} size={14} color="var(--muted-slate)" />{l}</span>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: TONES[tone].dot }} />
            </div>
            <Pill tone={tone} dot size="md">{tone === 'good' ? 'Operativo' : 'Atención'}</Pill>
            <div style={{ fontSize: 13, color: 'var(--body-muted)', marginTop: 12 }}>{metric}</div>
          </div>
        ))}
      </div>
      </MockBoundary>
      {/* dependency lanes */}
      <MockBoundary detail="Indicadores de demostración — sólo el feed en vivo de tareas usa datos reales." style={{ marginBottom: 24 }}>
      <Panel title="Cadena de entrega" right={<HelpDot text="Salud de cada tramo de la tubería, de la captura de señal a la entrega al espectador." />}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {lanes.map(([l, tone, v]) => (
            <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <span style={{ width: 200, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: TONES[tone].dot, flexShrink: 0 }} />{l}</span>
              <div style={{ flex: 1 }}><Progress value={v} color={tone === 'warn' ? 'var(--warn)' : 'var(--deep-green)'} h={8} /></div>
              <span className="t-mono" style={{ fontSize: 12, color: 'var(--muted-slate)', width: 44, textAlign: 'right' }}>{v}%</span>
              <Pill tone={tone} size="sm">{tone === 'good' ? 'OK' : 'Atención'}</Pill>
            </div>
          ))}
        </div>
      </Panel>
      </MockBoundary>
      <div className="circa-cols-8-4">
        <Panel title="Entrada de señal por canal" pad={0}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', padding: '12px 20px', borderBottom: '1px solid var(--border-cool)', fontSize: 12, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
            <span>Canal</span><span>Último segmento</span><span>Desfase</span><span>Estado</span>
          </div>
          {/* T318 — wired channel list. The "Último segmento" + "Desfase"
              metrics are not exposed by the public channel query in this
              milestone; honest em-dash placeholders. The status tone is
              the canonical paraphrase of the closed backend literal. */}
          {opsChannels === undefined ? (
            <SkeletonList rows={6} />
          ) : opsChannels.length === 0 ? (
            <EmptyState icon="radio" title="Sin canales" body="No hay canales en este espacio de trabajo." />
          ) : (
            opsChannels.slice(0, 6).map((c, i) => (
              <div key={c._id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', padding: '13px 20px', alignItems: 'center', borderBottom: i < Math.min(opsChannels.length, 6) - 1 ? '1px solid var(--lightest-gray)' : 'none', fontSize: 14 }}>
                <span>{c.name}</span>
                <span className="t-mono" style={{ fontSize: 12, color: 'var(--muted-slate)' }}>—</span>
                <span className="t-mono" style={{ fontSize: 12, color: 'var(--muted-slate)' }}>—</span>
                <Pill tone={c.backendStatus === 'error' ? 'bad' : c.backendStatus === 'running' ? 'good' : 'neutral'} size="sm" dot>{c.backendStatus === 'error' ? 'Sin señal' : c.backendStatus === 'running' ? 'Recibiendo' : '—'}</Pill>
              </div>
            ))
          )}
        </Panel>
        <MockBoundary detail="Indicadores de demostración — sólo el feed en vivo de tareas usa datos reales.">
        <Panel title="Incidentes recientes">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {[['Capacidad de borde alta', 'warn', 'hace 1 h', 'En curso'], ['Hueco de señal — Deportes Vivo', 'warn', 'hace 3 h', 'Resuelto'], ['Reinicio de nodo us-west-1', 'info', 'hace 8 h', 'Resuelto']].map((r, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 0', borderBottom: i < 2 ? '1px solid var(--lightest-gray)' : 'none' }}>
                <Icon name={r[1] === 'warn' ? 'alertTriangle' : 'info'} size={16} color={TONES[r[1]].fg} style={{ marginTop: 1 }} />
                <div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 500 }}>{r[0]}</div><span className="t-mono" style={{ fontSize: 10, color: 'var(--muted-slate)', textTransform: 'none' }}>{r[2]}</span></div>
                <Pill tone={r[3] === 'Resuelto' ? 'good' : 'warn'} size="sm">{r[3]}</Pill>
              </div>
            ))}
          </div>
        </Panel>
        </MockBoundary>
      </div>
      {/* Hardcoded background-task roster (names + fake "hace N min" + fixed
          "0 fallas" pill). The real live feed is the separate wired panel in
          SystemHealth.tsx (api.backgroundTasks.listRecent); this prototype
          roster has no backend behind it. */}
      <Panel decorative title="Tareas en segundo plano" pad={0} style={{ marginTop: 24 }}>
        {['Transcripción automática', 'Limpieza de transcripción', 'Extracción y publicación de audio', 'Enriquecimiento de contenido', 'Unión de señal'].map((t, i) => (
          <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 20px', borderBottom: i < 4 ? '1px solid var(--lightest-gray)' : 'none' }}>
            <Icon name="refresh" size={16} color="var(--muted-slate)" />
            <span style={{ flex: 1, fontSize: 14 }}>{t}</span>
            <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', width: 80, textAlign: 'right' }}>—</span>
            <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>hace {i + 1} min</span>
            <Pill tone="good" size="sm">0 fallas (7 d)</Pill>
          </div>
        ))}
      </Panel>
      </>}
    </div>
  );
}

// Spanish labels for the 7 backend trigger-event literals (convex/automations.ts).
const TRIGGER_EVENT_LABEL: Record<string, string> = {
  video_uploaded: 'Se sube un video',
  video_completed: 'Un video termina de procesarse',
  schedule_published: 'Se publica una programación',
  channel_started: 'Un canal empieza a transmitir',
  channel_stopped: 'Un canal deja de transmitir',
  alert_created: 'Se genera una alerta',
  slo_breached: 'Se incumple un SLO',
};
export function Automations() {
  const { toast } = useToast();
  void toast;
  // T318 — wired to api.automations.list. Renders honest skeleton/empty
  // states; no fixture fallback.
  // `trigger` is a backend OBJECT `{ event, filters? }` — rendering it
  // directly crashed the list (React #31). Extract `.event` → Spanish label.
  // `runs` reads the real `triggerCount`; there is no success-rate field on
  // the row, so we do not fabricate one.
  const wiredAuts = useQuery(api.automations.list, {}) as
    | Array<{ _id: string; name?: string; enabled?: boolean; trigger?: { event?: string } | string; triggerCount?: number; updatedAt?: number }>
    | undefined;
  const [auts, setAuts] = useState<Array<{ id: string; name: string; enabled: boolean; triggerEvent: string; runs: number; last: number | null }>>([]);
  React.useEffect(() => {
    if (wiredAuts === undefined) return;
    setAuts(
      wiredAuts.map((a) => {
        const event = typeof a.trigger === 'object' && a.trigger ? a.trigger.event : a.trigger;
        return {
          id: a._id,
          name: a.name ?? '—',
          enabled: a.enabled ?? false,
          triggerEvent: (event && TRIGGER_EVENT_LABEL[event]) || event || '—',
          runs: a.triggerCount ?? 0,
          last: a.updatedAt ?? null,
        };
      }),
    );
  }, [wiredAuts]);
  const { run: setEnabled } = useConvexMutationWithToast(api.automations.setEnabled, {
    logLabel: 'automation_set_enabled',
    errorMessagePrefix: 'No se pudo cambiar el estado',
  });
  const loading = useLoading(600);
  return (
    <div>
      <PageHead title="Automatizaciones" description="Ejecuta trabajo rutinario automáticamente. Cuando pasa algo, haz algo más."
        actions={<Btn variant="solid" icon="plus" onClick={() => navigate('/automations/new')}>Nueva automatización</Btn>} />
      {loading ? <><SkeletonStatRow /><div style={{ height: 36 }} /><SkeletonList rows={3} /></> : <>
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Activas" value={auts.filter(a => a.enabled).length} hint={`de ${auts.length} automatizaciones`} tone="good" icon="bolt" />
        <StatCard label="Disparos totales" value={auts.reduce((s, a) => s + a.runs, 0)} icon="activity" />
        <StatCard label="Total" value={auts.length} icon="bolt" />
        <StatCard label="Pausadas" value={auts.filter(a => !a.enabled).length} icon="pause" />
      </div>
      <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginBottom: 12 }}>TUS AUTOMATIZACIONES</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
        {auts.map(a => (
          <Panel key={a.id} pad={20}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Toggle on={a.enabled} onChange={v => { setAuts(x => x.map(y => y.id === a.id ? { ...y, enabled: v } : y)); void setEnabled({ id: a.id as any, enabled: v }); }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div onClick={() => navigate('/automations/' + a.id)} style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500, cursor: 'pointer' }}>{a.name}</div>
                <div style={{ fontSize: 13, color: 'var(--muted-slate)', marginTop: 2 }}>{a.triggerEvent} · {a.runs} {a.runs === 1 ? 'disparo' : 'disparos'}</div>
              </div>
              <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>{a.last ? ago(a.last) : '—'}</span>
              <Dropdown trigger={<Btn variant="ghost" icon="moreH" />} items={[{ label: 'Ver detalle', icon: 'eye', onClick: () => navigate('/automations/' + a.id) }, { label: 'Editar', icon: 'edit' }, { label: 'Duplicar', icon: 'copy' }, { divider: true }, { label: 'Eliminar', icon: 'trash', danger: true }]} />
            </div>
          </Panel>
        ))}
      </div>
      <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginBottom: 12 }}>EMPEZAR DESDE UNA PLANTILLA</div>
      {/* Template cards are decorative: clicking any one just navigates to the
          blank "Nueva automatización" builder — no real per-template payload
          is pre-filled, and there is no backend template catalog behind them. */}
      <MockBoundary detail="Plantillas ilustrativas — cada tarjeta sólo abre el formulario en blanco; no hay catálogo de plantillas en el backend.">
      <div className="circa-grid-3">
        {[['Etiquetar al subir', 'Cuando se sube un video, etiquétalo automáticamente.', 'tag'], ['Avisar si un canal cae', 'Notifica al equipo si un canal deja de transmitir.', 'bell'], ['Revisar cumplimiento', 'Ejecuta una verificación al terminar el procesamiento.', 'shield']].map(([t, d, ic]) => (
          <div key={t} onClick={() => navigate('/automations/new')} style={{ border: '1px dashed var(--border-cool)', borderRadius: 22, padding: 20, cursor: 'pointer', transition: 'border-color .15s' }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--deep-green)'} onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-cool)'}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--pale-green)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}><Icon name={ic} size={18} color="var(--deep-green)" /></div>
            <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4 }}>{t}</div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--muted-slate)', lineHeight: 1.45 }}>{d}</p>
            <div style={{ marginTop: 12 }}><Btn size="sm" variant="text" iconRight="arrowRight">Usar plantilla</Btn></div>
          </div>
        ))}
      </div>
      </MockBoundary>
      </>}
    </div>
  );
}

/**
 * AutomationBuilder — FR-008 / T327 (WIZ-4).
 *
 * Convex-wired creation surface for `api.automations.create`.
 *
 * Validator shape pinned at `convex/automations.ts:142-150` (and tested by
 * T326's automation-builder.test.tsx):
 *   - `name`: v.string()  (validator accepts empty, but FR-008 mandates we
 *     reject empty/whitespace at the form boundary so an unfindable row
 *     cannot reach the `automations` table)
 *   - `trigger`: { event: <one of 7 backend literals>, filters?: any }
 *   - `actions`: array of { kind: <one of 8 backend literals>, params: any }
 *
 * The 7 trigger-event literals (`convex/automations.ts:69-77`) and 8
 * action-kind literals (`convex/automations.ts:83-91`) are the backend
 * canonical IDs — the UI uses them DIRECTLY (no prototype `'upload'` →
 * `'video_uploaded'` mapping layer) so the call payload is validator-shaped
 * by construction.
 *
 * On submit:
 *   - if `name.trim().length === 0` → render `<p role="alert">` inline
 *     error and DO NOT invoke the mutation (honest-state / NFR-004)
 *   - otherwise → `createM.run({ name, trigger: { event }, actions:
 *     [{ kind, params: {} }] })`, then `navigate('/automations')` so the
 *     wired Automations list re-mounts and refreshes via Convex's reactive
 *     query subscription.
 *
 * The empty-`actions` array would also satisfy the validator; we send a
 * single default action so the created row is immediately useful (mirrors
 * the prototype's "etiquetar contenido" UX). An operator can edit the row
 * afterward to add more actions.
 */
export function AutomationBuilder() {
  const [name, setName] = useState('');
  // Default to the most common operator scenario ("video uploaded → tag");
  // both defaults are backend literals so the call payload is
  // validator-shaped from the first click.
  const [triggerEvent, setTriggerEvent] = useState<
    | 'video_uploaded'
    | 'video_completed'
    | 'schedule_published'
    | 'channel_started'
    | 'channel_stopped'
    | 'alert_created'
    | 'slo_breached'
  >('video_uploaded');
  const [actionKind, setActionKind] = useState<
    | 'tag_asset'
    | 'notify_slack'
    | 'notify_email'
    | 'enqueue_compliance_check'
    | 'create_alert'
    | 'set_channel_status'
    | 'publish_schedule'
    | 'dry_run_only'
  >('tag_asset');
  const [nameError, setNameError] = useState<string | null>(null);

  const createM = useConvexMutationWithToast(api.automations.create, {
    logLabel: 'automations_create',
    mutationName: 'automations.create',
    successMessage: 'Automatización creada.',
    errorMessagePrefix: 'No se pudo crear la automatización',
  });

  // UI metadata for the trigger picker — backend literal id, Spanish operator
  // label, primitive icon name. The id flows directly into the
  // `trigger.event` payload.
  const triggers: Array<[typeof triggerEvent, string, string]> = [
    ['video_uploaded', 'Se sube un video', 'upload'],
    ['video_completed', 'Un video termina de procesarse', 'checkCircle'],
    ['schedule_published', 'Se publica una programación', 'send'],
    ['channel_started', 'Un canal empieza a transmitir', 'play'],
    ['channel_stopped', 'Un canal deja de transmitir', 'pause'],
    ['alert_created', 'Se genera una alerta', 'bell'],
    ['slo_breached', 'Se incumple un SLO', 'alertTriangle'],
  ];

  // UI metadata for the action picker — backend literal kind + Spanish label.
  // `Select` is keyed by value, so we pass `{ value, label }` objects.
  const actionOptions: Array<{ value: typeof actionKind; label: string }> = [
    { value: 'tag_asset', label: 'Etiquetar contenido' },
    { value: 'notify_slack', label: 'Notificar por Slack' },
    { value: 'notify_email', label: 'Notificar por correo' },
    { value: 'enqueue_compliance_check', label: 'Encolar revisión de cumplimiento' },
    { value: 'create_alert', label: 'Crear una alerta' },
    { value: 'set_channel_status', label: 'Cambiar el estado de un canal' },
    { value: 'publish_schedule', label: 'Publicar una programación' },
    { value: 'dry_run_only', label: 'Simulación (sin efecto)' },
  ];

  const handleSave = async () => {
    // Form-boundary validation. The backend validator at
    // `convex/automations.ts:143` accepts `v.string()` with no min-length,
    // so an empty name would round-trip an unfindable row to the database
    // (NFR-004 / FR-008 honest-state). Trim-then-check catches the
    // whitespace-only case the same way the prototype's empty path would.
    if (name.trim().length === 0) {
      setNameError('Escribe un nombre para la automatización.');
      return;
    }
    setNameError(null);
    try {
      await createM.run({
        name: name.trim(),
        trigger: { event: triggerEvent },
        actions: [{ kind: actionKind, params: {} }],
      });
      // Reactive `api.automations.list` query on the Automations screen
      // will re-render with the new row automatically.
      navigate('/automations');
    } catch {
      // The wrapper already emitted the error toast + structured
      // `_failed` log; swallow here so the operator stays on the form
      // with their typed state intact.
    }
  };

  const previewLabel =
    triggers.find((t) => t[0] === triggerEvent)?.[1].toLowerCase() ?? '';
  const previewAction =
    actionOptions.find((a) => a.value === actionKind)?.label.toLowerCase() ?? '';

  return (
    <div className="circa-cols-8-4">
      <div>
        <PageHead
          crumbs={[
            { label: 'Automatizaciones', href: '#/automations' },
            { label: 'Nueva' },
          ]}
          title="Nueva automatización"
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Panel title="1 · Lo básico">
            <Field label="Nombre" required>
              <Input
                placeholder="Nombre de la automatización"
                value={name}
                onChange={(v: string) => {
                  setName(v);
                  if (nameError && v.trim().length > 0) setNameError(null);
                }}
              />
            </Field>
            {nameError && (
              <p
                role="alert"
                style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--bad)' }}
              >
                {nameError}
              </p>
            )}
          </Panel>
          <Panel title="2 · Cuándo se activa">
            <div className="circa-grid-2-tight">
              {triggers.map(([id, label, icon]) => (
                <button
                  key={id}
                  onClick={() => setTriggerEvent(id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    border: `1px solid ${triggerEvent === id ? 'var(--deep-green)' : 'var(--border-cool)'}`,
                    background: triggerEvent === id ? 'var(--pale-green)' : 'transparent',
                    borderRadius: 14,
                    padding: '14px 16px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 14,
                  }}
                >
                  <Icon
                    name={icon}
                    size={18}
                    color={triggerEvent === id ? 'var(--deep-green)' : 'var(--muted-slate)'}
                  />
                  {label}
                </button>
              ))}
            </div>
          </Panel>
          <Panel title="3 · Qué hacer (acción)">
            <Field label="Acción">
              <Select
                value={actionKind}
                onChange={(v: string) => setActionKind(v as typeof actionKind)}
                full
                options={actionOptions}
              />
            </Field>
          </Panel>
          <div>
            <Btn
              variant="solid"
              loading={createM.loading}
              onClick={handleSave}
            >
              Guardar automatización
            </Btn>
          </div>
        </div>
      </div>
      <Panel title="Vista previa">
        <div
          style={{
            padding: 16,
            background: 'var(--snow)',
            borderRadius: 14,
            fontSize: 14,
            lineHeight: 1.7,
          }}
        >
          <b>Cuando</b> {previewLabel},<br />
          <b>entonces</b> {previewAction}.
        </div>
      </Panel>
    </div>
  );
}

export function AlertsList() {
  const { toast } = useToast();
  const [sev, setSev] = useState('Todas');
  // T318 — wired to api.alerts.list. Renders honest skeleton/empty
  // states; the prototype's resolved-filter is computed off the wired
  // row when the public surface provides a `resolvedAtMs` field.
  const wiredAlertsList = useQuery(api.alerts.list, {}) as
    | Array<{ _id: string; severity?: 'critical' | 'warning' | 'info'; message?: string; createdAtMs?: number; resolvedAtMs?: number; channelId?: string }>
    | undefined;
  const [alerts, setAlerts] = useState<Array<{ id: string; sev: string; text: string; ts: number; resolved: boolean; channel?: string | null }>>([]);
  React.useEffect(() => {
    if (wiredAlertsList === undefined) return;
    setAlerts(
      wiredAlertsList
        .filter((a) => a.resolvedAtMs === undefined)
        .map((a) => ({
          id: a._id,
          sev: a.severity ?? 'info',
          text: a.message ?? '—',
          ts: a.createdAtMs ?? Date.now(),
          resolved: a.resolvedAtMs !== undefined,
          channel: a.channelId ?? null,
        })),
    );
  }, [wiredAlertsList]);
  const sevs = ['Todas', 'Crítico', 'Advertencia', 'Información'];
  const map = { 'Crítico': 'critical', 'Advertencia': 'warning', 'Información': 'info' };
  const shown = alerts.filter(a => sev === 'Todas' || a.sev === map[sev]);
  const loading = useLoading(600);
  return (
    <div>
      <PageHead title="Alertas" description="Todos los asuntos abiertos en tu espacio de trabajo." actions={<Btn variant="ghost" iconRight="arrowRight" onClick={() => navigate('/settings/notifications')}>Preferencias</Btn>} />
      {loading ? <><SkeletonStatRow /><div style={{ height: 56 }} /><SkeletonList rows={5} /></> : <>
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Críticas" value={alerts.filter(a => a.sev === 'critical').length} tone={alerts.some(a => a.sev === 'critical') ? 'bad' : undefined} icon="alertCircle" />
        <StatCard label="Advertencias" value={alerts.filter(a => a.sev === 'warning').length} tone="warn" icon="alertTriangle" />
        <StatCard label="Informativas" value={alerts.filter(a => a.sev === 'info').length} icon="info" />
        <StatCard label="Tiempo medio de resolución" value="14 min" icon="clock" hint="últimas 24 h" />
      </div>
      <Panel pad={16} style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {sevs.map(s => <button key={s} onClick={() => setSev(s)} style={{ border: '1px solid var(--border-cool)', cursor: 'pointer', borderRadius: 999, padding: '6px 14px', fontSize: 13, fontFamily: 'var(--font-sans)',
            background: sev === s ? 'var(--ink-muted)' : 'transparent', color: sev === s ? '#fff' : 'var(--slate)', borderColor: sev === s ? 'var(--ink-muted)' : 'var(--border-cool)' }}>{s}</button>)}
        </div>
      </Panel>
      <Panel pad={0}>
        {shown.length === 0 && <div style={{ padding: 24 }}><EmptyState icon="checkCircle" title="Sin alertas abiertas" body="Todo está en orden en tu espacio de trabajo." /></div>}
        {shown.map((a, i) => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', borderBottom: i < shown.length - 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
            <Icon name={a.sev === 'critical' ? 'alertCircle' : a.sev === 'warning' ? 'alertTriangle' : 'info'} size={18} color={TONES[a.sev === 'critical' ? 'bad' : a.sev === 'warning' ? 'warn' : 'info'].fg} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 3 }}>
                <span style={{ fontWeight: 500, fontSize: 14 }}>{a.friendly}</span>
                {a.channel && <Pill tone="neutral" size="sm">{a.channel}</Pill>}
              </div>
              <span style={{ fontSize: 13, color: 'var(--body-muted)' }}>{a.text}</span>
            </div>
            <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>{ago(a.ts)}</span>
            <Btn size="sm" variant="ghost" onClick={() => setAlerts(x => x.map(y => y.id === a.id ? { ...y, read: true } : y))}>Reconocer</Btn>
            <Btn size="sm" variant="text" onClick={() => { setAlerts(x => x.filter(y => y.id !== a.id)); toast({ variant: 'success', message: 'Alerta resuelta.' }); }}>Resolver</Btn>
          </div>
        ))}
      </Panel>
      </>}
    </div>
  );
}

export function BatchOps() {
  const { toast } = useToast();
  const [tab, setTab] = useState('Activos');
  const [wiz, setWiz] = useState(false);
  return (
    <div>
      <PageHead title="Operaciones masivas" description="Aplica cambios a muchos videos, programaciones o canales a la vez." actions={<Btn variant="solid" icon="plus" onClick={() => setWiz(true)}>Nuevo trabajo</Btn>} />
      <Tabs tabs={['Activos', 'Historial']} active={tab} onChange={setTab} style={{ marginBottom: 24 }} />
      {tab === 'Activos' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[['Aplicar plantilla “Pre-roll” a 24 videos', 18, 24, 'Ejecutando'], ['Re-etiquetar biblioteca de noticias', 96, 96, 'En cola']].map(([title, done, total, st], i) => (
            <Panel key={i} pad={20}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ fontWeight: 500, fontSize: 15 }}>{title}</span>
                <Pill tone={st === 'Ejecutando' ? 'info' : 'neutral'} dot={st === 'Ejecutando'}>{st}</Pill>
              </div>
              <Progress value={(done / total) * 100} />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
                <span style={{ fontSize: 12, color: 'var(--muted-slate)' }}>{done}/{total} procesados · 0 fallidos</span>
                <div style={{ display: 'flex', gap: 8 }}><Btn size="sm" variant="ghost">Pausar</Btn><Btn size="sm" variant="text">Cancelar</Btn></div>
              </div>
            </Panel>
          ))}
        </div>
      ) : (
        <Panel pad={0}>
          {[['Aplicar metadatos', 120, 'Completado', 'good'], ['Mover a papelera', 8, 'Parcial', 'warn'], ['Re-codificar', 40, 'Cancelado', 'neutral']].map(([op, n, res, tone], i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px', borderBottom: i < 2 ? '1px solid var(--lightest-gray)' : 'none', fontSize: 14 }}>
              <span style={{ flex: 1 }}>{op}</span><span style={{ color: 'var(--muted-slate)' }}>{n} elementos</span><Pill tone={tone} size="sm">{res}</Pill><Btn size="sm" variant="text">Ver reporte</Btn>
            </div>
          ))}
        </Panel>
      )}
      <BatchPatternWizard open={wiz} onClose={() => setWiz(false)} />
    </div>
  );
}

/**
 * T333 (FR-012, WIRE-7) — `/compliance` deferred honestly.
 *
 * `convex/compliance.ts` exports a per-asset rule-check query
 * (`checkAsset` and `checkSchedule`) plus an `autoFix` mutation, but
 * there is no public `listAssets` / `recheckAll` aggregator that the
 * workspace-wide compliance dashboard requires. Per FR-012 / NFR-006
 * this surface defers honestly until the aggregate query family lands.
 *
 * The previous prototype rendered hardcoded counts (4 aprobados / 1
 * advertencia / 1 fallido) and fabricated rule rows from `CIRCA.assets`.
 * Both anti-patterns are removed in lockstep.
 *
 * The per-asset rule-check surface (`api.compliance.checkAsset`) remains
 * available for callers that pass an `assetId` (the content-detail view
 * is the natural integration point; not wired this milestone).
 */
export function Compliance() {
  return (
    <div>
      <PageHead title="Cumplimiento" description="Asegúrate de que cada video cumpla tus reglas antes de salir al aire." />
      <Panel>
        <EmptyState
          icon="shield"
          title="Próximamente — Cumplimiento agregado"
          body="La vista agregada de cumplimiento (videos aprobados, advertencias, fallidos y cobertura por regla) llegará cuando se publiquen las consultas agregadas correspondientes. Mientras tanto, las reglas se verifican por video al subirlo y antes de cada publicación."
          action={<Btn variant="ghost" iconRight="arrowRight" onClick={() => navigate('/library')}>Ir a la biblioteca</Btn>}
        />
      </Panel>
    </div>
  );
}

/**
 * T333 (FR-012, WIRE-7) — `/version-history` wired to `api.versionHistory.list`.
 *
 * The public `api.versionHistory.list` query is workspace-wide when
 * `entityType` and `entityId` are both omitted (default cap 200 rows,
 * newest-first), and indexed when both are provided. This view subscribes
 * to the workspace-wide feed and derives the three entity-class counts
 * (channels / schedules / assets) from the rows themselves — honest
 * counts replace the prototype's hardcoded "6/6/18".
 *
 * The per-entity drill-down (the existing `VersionDetailModal`) is opened
 * with the entity label as before; that modal remains a presentational
 * stub for now because it queries the per-entity indexed path which
 * requires an `entityId`, not just a class — a future wave wires the
 * picker UI for "pick a channel/schedule/video and see its history".
 */
type VersionHistoryEntityType =
  | 'channel'
  | 'schedule'
  | 'scheduleItem'
  | 'marker'
  | 'asset'
  | 'edge';

export function VersionHistory() {
  const events = useQuery(api.versionHistory.list, {}) as
    | Array<{ _id: string; entityType: VersionHistoryEntityType; entityId: string; createdAt: number; action?: string; userName?: string }>
    | undefined;
  const [entity, setEntity] = useState<string | null>(null);

  const loading = events === undefined;

  // Distinct entity counts per class (operator-facing labels). Schedules
  // bucket includes both `schedule` and `scheduleItem`. The cards still
  // render when the bucket is zero — the count is the honest truth.
  const distinctIdsByClass = (() => {
    if (!events) return null;
    const channels = new Set<string>();
    const schedules = new Set<string>();
    const assets = new Set<string>();
    for (const e of events) {
      if (e.entityType === 'channel') channels.add(e.entityId);
      else if (e.entityType === 'schedule' || e.entityType === 'scheduleItem') schedules.add(e.entityId);
      else if (e.entityType === 'asset') assets.add(e.entityId);
    }
    return { channels: channels.size, schedules: schedules.size, assets: assets.size };
  })();

  return (
    <div>
      <PageHead title="Historial de versiones" description="Viaja en el tiempo por tus canales, programaciones y videos." />
      {loading ? <SkeletonCardGrid n={3} /> : (
        <>
          <div className="circa-grid-3" style={{ marginBottom: 24 }}>
            {([
              ['Canales', 'tv', distinctIdsByClass!.channels],
              ['Programaciones', 'calendar', distinctIdsByClass!.schedules],
              ['Videos', 'film', distinctIdsByClass!.assets],
            ] as Array<[string, string, number]>).map(([title, icon, count]) => (
              <Panel key={title} pad={24} style={{ cursor: 'pointer' }}>
                <div onClick={() => setEntity(title)} style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--snow)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={icon} size={22} color="var(--deep-green)" /></div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 500 }}>{title}</div>
                    <div style={{ fontSize: 13, color: 'var(--muted-slate)' }}>
                      {count === 0 ? 'Sin entidades con historial todavía' : `${count} ${count === 1 ? 'entidad con historial' : 'entidades con historial'}`}
                    </div>
                  </div>
                  <Icon name="chevronR" size={18} color="var(--muted-slate)" />
                </div>
              </Panel>
            ))}
          </div>
          <Panel title="Actividad reciente del espacio" pad={0}>
            {events && events.length > 0 ? events.slice(0, 20).map((e, i) => (
              <div key={e._id} style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 1fr 1fr', padding: '13px 20px', alignItems: 'center', borderBottom: i < Math.min(events.length, 20) - 1 ? '1px solid var(--lightest-gray)' : 'none', fontSize: 14 }}>
                <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', textTransform: 'none' }}>{new Date(e.createdAt).toLocaleString('es-MX')}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.action ?? '—'}</span>
                <Pill tone="neutral" size="sm">{e.entityType}</Pill>
                <span style={{ fontSize: 13, color: 'var(--muted-slate)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.userName ?? '—'}</span>
              </div>
            )) : (
              <div style={{ padding: 24 }}>
                <EmptyState icon="clock" title="Sin actividad registrada" body="Aún no se han registrado cambios en este espacio de trabajo." />
              </div>
            )}
          </Panel>
        </>
      )}
      <VersionDetailModal open={!!entity} onClose={() => setEntity(null)} entity={entity || ''} />
    </div>
  );
}

